import React, { memo } from 'react';
import PropTypes from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import { ProductSummary, ProductSummaryWithAddToBag, ProductSummaryWithQuickAdd, ProductSummaryWithOverlay } from '@ynap/product-summary';
import { useAppState } from '@ynap/immutables-provider';
import { performance } from '@ynap/clientside-monitoring';
import { linkRenderer } from '@ynap/router';
import { useConfig } from '@ynap/immutables-provider';

const bem = new BEMHelper('RecommendationsProducts');

const RecommendationsProduct = ({
    recommendedProduct,
    index,
    messages,
    onClick = () => {},
    type,
    modifier,
    tracking,
    isWithAddToBag = false,
    addToCart,
    withQuickAdd = false,
    isLastAdded = false,
    inProgress = false,
    noDescription,
    isWishlistRecommendations,
    overlayProductId,
    handleOverlayProductIdChange,
    setIsPhoneOverlay,
    CTAButtonsState,
    drawerTracking,
    positionDrawer,
    doubleImageWithoutPadding,
    doubleImageModifier,
    reducedMode,
    details,
    omnibusAlignment,
    onRemove,
}) => {
    if (!recommendedProduct || Object.keys(recommendedProduct).length === 0) return null;
    const config = useConfig();
    const urlConfig = config?.global?.urlConfig;
    const pageRoutes = config?.head?.routesAllowlist?.pageRoutes;
    const { image = {}, designer, description, productId, partNumber, href, price, badge, consideredBadge } = recommendedProduct;
    const { alt, primary, primarySrcset, secondary, secondarySrcset } = image;
    const { locale, addToCart: addToBagState = {}, country } = useAppState();

    const originStrippedHref = href.replace(window.location.origin, '');

    const WithLink = ({ children }) =>
        linkRenderer(
            urlConfig,
            pageRoutes,
            locale,
            originStrippedHref,
            children,
            () => {
                onClick(recommendedProduct, index + 1);
                performance.intTiming.markSpaInteractionTime('pdp_to_pdp');
                drawerTracking?.();
                positionDrawer?.();
            },
            bem('product', [type, reducedMode ? 'reducedMode' : ''], { isWithAddToBag }),
            false,
            window.location.hostname,
        );

    return (
        <>
            {isWithAddToBag ? (
                <WithLink>
                    <ProductSummaryWithAddToBag
                        classNameButton={bem('addToBagBtn')}
                        skuPartNumber={recommendedProduct.partNumber}
                        product={{
                            ...recommendedProduct,
                            imgSrc: {
                                alt,
                                primary,
                                primarySrcset,
                                secondary,
                                secondarySrcset,
                            },
                            designerName: designer,
                            name: description,
                            partNumber,
                            href,
                            price,
                            badge,
                            onSale: false,
                            consideredBadge,
                            isBuyable: !addToBagState.loading,
                        }}
                        messages={messages}
                        addToCart={addToCart}
                        addToCartLoading={addToBagState.loading}
                        addToCartError={addToBagState.error}
                        openProductDetail={() => tracking.ymal(recommendedProduct.partNumber)}
                    />
                </WithLink>
            ) : withQuickAdd ? (
                <ProductSummaryWithQuickAdd
                    classNameButton={bem('a')}
                    skuPartNumber={recommendedProduct.partNumber}
                    product={{
                        ...recommendedProduct,
                        imgSrc: {
                            alt,
                            primary,
                            primarySrcset,
                            secondary,
                            secondarySrcset,
                        },
                        designerName: designer,
                        name: description,
                        partNumber,
                        href,
                        price,
                        badge,
                        onSale: false,
                        consideredBadge,
                        isBuyable: !addToBagState.loading,
                    }}
                    messages={messages}
                    addToCart={addToCart}
                    openProductDetail={() => tracking.ymal(recommendedProduct.partNumber)}
                    isLastAdded={isLastAdded}
                    inProgress={inProgress}
                />
            ) : isWishlistRecommendations ? (
                <ProductSummaryWithOverlay
                    product={{
                        ...recommendedProduct,
                        imgSrc: {
                            alt,
                            primary,
                            primarySrcset,
                            secondary,
                            secondarySrcset,
                        },
                        designerName: designer,
                        name: description,
                        productId,
                        partNumber,
                        href,
                        price,
                        badge,
                        onSale: false,
                        consideredBadge,
                    }}
                    imageSrcsetSize="(max-width: 1024px) 40vw, 20vw" // TODO: https://jira.nap/browse/SHOP-519
                    messages={messages}
                    modifier={modifier}
                    nameModifier={noDescription ? 'noDescriptionStyle' : 'oneLine'}
                    type={type}
                    locale={locale}
                    overlayProductId={overlayProductId}
                    handleOverlayProductIdChange={handleOverlayProductIdChange}
                    setIsPhoneOverlay={setIsPhoneOverlay}
                    CTAButtonsState={CTAButtonsState}
                    isWishlist={isWishlistRecommendations}
                />
            ) : (
                <WithLink>
                    <ProductSummary
                        product={{
                            imgSrc: {
                                alt,
                                primary,
                                primarySrcset,
                                secondary,
                                secondarySrcset,
                            },
                            designerName: designer,
                            name: description,
                            productId,
                            partNumber,
                            href,
                            price,
                            badge,
                            onSale: false,
                            consideredBadge,
                        }}
                        imageSrcsetSize="(max-width: 1024px) 40vw, 20vw" // TODO: https://jira.nap/browse/SHOP-519
                        messages={messages}
                        modifier={modifier}
                        nameModifier={noDescription ? 'noDescriptionStyle' : 'oneLine'}
                        type={type}
                        locale={locale}
                        doubleImageWithoutPadding={doubleImageWithoutPadding}
                        doubleImageModifier={doubleImageModifier}
                        details={details}
                        omnibusAlignment={omnibusAlignment}
                        country={country}
                        imageOverlayElement={
                            onRemove && (
                                <div className={bem('buttonContainer')}>
                                    <button
                                        className={bem('removeButton')}
                                        type="button"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            event.preventDefault();
                                            onRemove(recommendedProduct);
                                        }}
                                    />
                                </div>
                            )
                        }
                    />
                </WithLink>
            )}
        </>
    );
};

RecommendationsProduct.propTypes = {
    recommendedProduct: PropTypes.object,
    index: PropTypes.number,
    messages: PropTypes.shape({
        wearItWith: PropTypes.shape({
            heading: PropTypes.func.isRequired,
        }).isRequired,
        youMayAlsoLike: PropTypes.shape({
            heading: PropTypes.func.isRequired,
        }).isRequired,
    }),
    tracking: PropTypes.func,
    onClick: PropTypes.func.isRequired,
    addToBagState: PropTypes.object,
    addToCart: PropTypes.func,
    type: PropTypes.string,
    modifier: PropTypes.string,
    isWithAddToBag: PropTypes.bool,
    withQuickAdd: PropTypes.bool,
    isLastAdded: PropTypes.bool,
    inProgress: PropTypes.bool,
    isWishlistRecommendations: PropTypes.bool,
    overlayProductId: PropTypes.string,
    handleOverlayProductIdChange: PropTypes.func,
    drawerTracking: PropTypes.func,
    setIsPhoneOverlay: PropTypes.func,
    doubleImageWithoutPadding: PropTypes.string,
    positionDrawer: PropTypes.func,
    reducedMode: PropTypes.bool,
    details: PropTypes.bool,
    omnibusAlignment: PropTypes.bool,
    onRemove: PropTypes.func,
};

RecommendationsProduct.defaultProps = {
    recommendedProduct: {},
    index: 0,
    messages: {},
};

export default memo(RecommendationsProduct, (prevProps, nextProps) => {
    return prevProps.recommendedProduct === nextProps.recommendedProduct;
});
